import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthenticationService } from 'app/services';
import { environment } from 'environments/environment';
import moment from 'moment-timezone';
import { getCurrentBrand } from 'app/config/brands';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(private authenticationService: AuthenticationService) { }

    private async hmacSign(secret: string, message: string): Promise<string> {
        const enc = new TextEncoder();
        const key = await crypto.subtle.importKey(
            'raw',
            enc.encode(secret),
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign']
        );
        const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
        return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
    }


    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const user = this.authenticationService.currentUserData;

        // Brand of this page for the Node API (#183). The API trusts the calling
        // domain first; the header only matters on plain localhost (?brand=...).
        if (request.url.indexOf(environment.apiUrl) > -1) {
            request = request.clone({ setHeaders: { 'X-Brand': getCurrentBrand().key } });
        }

        if (user && user.access_token) {
            // https://gitlab.optimasit.com/arsl/optima-crm-v2/-/issues/2859
            /**
             * add this check if request from CRM it add the default header
             * else it add the empty header
             * 
             * NOTE 
             * why we add because when ever we call the google-translation API 
             * as the result we are logout from CRM
             * 
            */
            if (request.url.indexOf('3.127.214.192') > -1 || request.url.indexOf('immosurance') > -1 || request.url.indexOf('immokredita') > -1 || request.url.indexOf('localhost') > -1 || request.url.indexOf('127.0.0.1') > -1) {
                if (
                    request.url.indexOf('csv-account-uploader') > -1 ||
                    request.url.indexOf('properties/csv-properties-uploader') > -1 ||
                    request.url.indexOf('single-upload') > -1 ||
                    request.url.indexOf('xml-export-formats/csv-company-uploader') > -1 ||
                    request.url.indexOf('xml-export-formats/csv-contacts-uploader') > -1 ||
                    request.url.indexOf('users/upload-single') > -1 ||
                    request.url.indexOf('xml-export-formats/csv-owner-uploader') > -1 ||
                    request.url.indexOf('xml-export-formats/csv-timeline-uploader') > -1 ||
                    request.url.indexOf('cms-medias/uploader') > -1 ||
                    request.url.indexOf('import/multi-format-importer') > -1
                ) {
                    request = request.clone({});
                } else if (
                    request.url.indexOf('media/create') > -1 ||
                    request.url.indexOf('media/update') > -1 ||
                    request.url.indexOf('mail-attachment') > -1 ||
                    request.url.indexOf('translations/uploade-translations') > -1 ||
                    request.url.indexOf('attachments/upload-csv') > -1 ||
                    request.url.indexOf('attachments/uploader') > -1 || // Enable auth in uploaded docs
                    request.url.indexOf('common/import-csv') > -1 ||
                    request.url.indexOf('faqs/import') > -1
                ) {
                    request = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${user.access_token}`
                        }
                    });
                } else if (request.url.indexOf(environment.apiUrl) > -1) {
                    request = request.clone({
                        setHeaders: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.access_token}`,
                            'Time-Zone': moment.tz.guess(),
                        }
                    });
                }  else if (
                    request.url.indexOf('send-mail-to-mailbox') > -1 ||
                    request.url.indexOf('emails/send') > -1 ||
                    request.url.indexOf('emails/send-multiple-mails') > -1 ||
                    request.url.indexOf('pdf/send-pdf-mail') > -1 ||
                    request.url.indexOf('emails/send-invoice-email') > -1
                ) {
                    const timestamp = Math.floor(Date.now() / 1000).toString();
                    const bodyStr   = request.body ? (typeof request.body === 'string' ? request.body : JSON.stringify(request.body)): '';
                    return from(this.hmacSign(environment.mailboxInternalApiKey, `${timestamp}.${bodyStr}`)).pipe(
                        switchMap(signature => next.handle(request.clone({
                            setHeaders: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${user.access_token}`,
                                'X-Timestamp': timestamp,
                                'X-Signature': signature
                            }
                        })))
                    );
                } else {
                    request = request.clone({
                        setHeaders: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.access_token}`
                        }
                    });
                }
            } else {
                request = request.clone({});
            }
        }
        return next.handle(request);
    }
}
